// Currency / number / date formatting helpers.
import dayjs from 'dayjs';

// Built once. `formatRupiah` now runs per table row on the rent list, and
// constructing an Intl formatter costs far more than formatting with one.
const ID_NUMBER = new Intl.NumberFormat('id-ID');

interface FormatRupiahOptions {
  /**
   * Keep the space between the "Rp" prefix and the amount. Defaults to true
   * ("Rp 308.000.000"); pass false for designs that render it tight
   * ("Rp308.000").
   */
  space?: boolean;
}

/**
 * Date plus wall-clock time, e.g. "01/08/2026 23:58". For audit columns where
 * the hour carries meaning; use a date-only formatter when it does not.
 */
export const formatDateTime = (
  value?: string | null,
  fallback = '-'
): string => {
  if (!value) return fallback;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : fallback;
};

export const formatDate = (value?: string | null, fallback = '-'): string => {
  if (!value) return fallback;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : fallback;
};

/**
 * Format a byte count for display, e.g. 1572864 -> "1.5 MB".
 */
export const formatBytes = (bytes: number, decimals = 2): string => {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Group a digit string for display, e.g. "100000" -> "100.000".
 *
 * Takes and returns strings so it can drive a controlled money input while
 * the raw digits stay in state. Never store the formatted result: JS parses
 * "100.000" as 100, so a grouped string reaching Number() silently loses
 * three orders of magnitude.
 */
export const formatThousands = (digits: string): string => {
  const cleaned = (digits ?? '').replace(/\D/g, '');
  if (!cleaned) return '';
  // Drop leading zeros so "007" reads as "7", but keep a lone "0".
  const normalised = cleaned.replace(/^0+(?=\d)/, '');
  return ID_NUMBER.format(Number(normalised));
};

/**
 * Format a number as Indonesian Rupiah, e.g. 308000000 -> "Rp 308.000.000".
 * Returns the fallback (default "-") for null/undefined/non-finite input.
 */
export const formatRupiah = (
  value: number | null | undefined,
  fallback = '-',
  options: FormatRupiahOptions = {}
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return fallback;
  }
  const { space = true } = options;
  return `Rp${space ? ' ' : ''}${ID_NUMBER.format(value)}`;
};

const ID_COMPACT = new Intl.NumberFormat('id-ID', { maximumFractionDigits: 1 });

/**
 * "Rp12,1 jt" / "Rp1,2 M" / "Rp950 rb" for figures that must share a card
 * line. Below a thousand it is the full amount, and the sign is kept.
 */
export const formatRupiahCompact = (value: number): string => {
  const abs = Math.abs(value);
  const [scaled, unit] =
    abs >= 1e9
      ? [abs / 1e9, ' M']
      : abs >= 1e6
        ? [abs / 1e6, ' jt']
        : abs >= 1e3
          ? [abs / 1e3, ' rb']
          : [abs, ''];
  return `${value < 0 ? '\u2212' : ''}Rp${ID_COMPACT.format(scaled)}${unit}`;
};

/**
 * A `Date` as the API's `YYYY-MM-DD`, read in the BROWSER's calendar.
 *
 * Never `toISOString()`: that converts to UTC first, so a date picked in WIB
 * lands on the previous day for anyone the wrong side of midnight — and these
 * values filter plain `date` columns, which have no timezone to correct it with.
 */
export const formatDateToApi = (date: Date | null | undefined): string => {
  if (!date) return '';
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * A picked range as the two API date bounds, with "All Time" spelled out.
 *
 * The rent money endpoints read an ABSENT bound as "not given" and fill in
 * their own default (today, or the current month), so an empty range would
 * silently narrow to that default instead of widening to everything. Both
 * screens that clear a range need the same widening, and the rule is not
 * guessable from the call site — so it lives here, once.
 */
export const toApiDateRange = (
  range: { from: Date | null; to: Date | null },
  today: string
): { startDate: string; endDate: string } => ({
  startDate: range.from ? formatDateToApi(range.from) : ALL_TIME_START_DATE,
  endDate: range.to ? formatDateToApi(range.to) : today,
});

/**
 * Whole days from one YYYY-MM-DD to another. Uses dayjs, the day-arithmetic
 * idiom everywhere else in this repo, rather than a second hand-rolled one —
 * `format.ts` should not hold two different rules about what a day is.
 */
export const daysBetweenApiDates = (from: string, to: string): number =>
  dayjs(to).diff(dayjs(from), 'day');

/**
 * Predates any real rental data — the explicit lower bound for "All Time".
 */
export const ALL_TIME_START_DATE = '2000-01-01';

/**
 * A timestamp in WIB, which is the calendar every rent money figure is quoted
 * in. `withDate` is what separates a list that sits under a "today" header
 * (time alone) from one that spans days.
 */
export const formatWibDateTime = (
  value: string | null,
  { withDate = false }: { withDate?: boolean } = {}
): string => {
  if (!value) return '-';
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return '-';
  return when.toLocaleString('id-ID', {
    ...(withDate
      ? {
          day: '2-digit' as const,
          month: 'short' as const,
          year: 'numeric' as const,
        }
      : {}),
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Jakarta',
  });
};
